/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import cloneDeep from "lodash/fp/cloneDeep";
import merge from "lodash/fp/merge";

import { RequestStatus } from "~/constants";
import {
  OpenWhitelabelBuilderPayloadType,
  SitebuilderState,
  UpdateSkinConfigPayloadType,
} from "~/features/sitebuilder/types";

const initialState: SitebuilderState = {
  preview: {
    meta: {
      initStatus: RequestStatus.IDLE,
      publishStatus: RequestStatus.IDLE,
    },
    data: {
      isOpen: false,
      isNewSite: false,
      reviewId: null,
      skinReviewId: 0,
      status: 0,
      details: {
        defaultSkinConfig: null,
        skinConfig: null,
        siteName: "",
        payoutType: 0,
        siteId: 0,
        newSiteReviewId: 0,
        needsReview: false,
        publishComment: "",
      },
    },
  },
  uploads: {
    meta: {
      uploadStatus: RequestStatus.IDLE,
    },
  },
};

const sitebuilderSlice = createSlice({
  name: "sitebuilder",
  initialState,
  reducers: {
    resetSitebuilderData: (state) => {
      state.preview.data = cloneDeep(initialState.preview.data);
      state.uploads = cloneDeep(initialState.uploads);
    },
    updateSkinConfig: (state, action: { payload: UpdateSkinConfigPayloadType }) => {
      state.preview.data.details = merge(state.preview.data.details, action.payload);
    },
    openSitebuilder: (state, action: PayloadAction<OpenWhitelabelBuilderPayloadType>) => {
      state.preview.data.isOpen = true;
      state.preview.data.reviewId = action.payload.reviewId;
      state.preview.data.isNewSite = action.payload.isNewSite;
      state.preview.data.details.skinConfig = merge(state.preview.data.details.skinConfig, action.payload.skinConfig);
      state.preview.data.details.siteId = action.payload.siteId;
      state.preview.data.skinReviewId = action.payload.skinReviewId;
      state.preview.data.status = action.payload.status;
    },
    closeSitebuilder: (state) => {
      state.preview.data.isOpen = false;
    },
    resetToDefault: (state) => {
      state.preview.data.details.skinConfig = state.preview.data.details.defaultSkinConfig;
    },
  },
});

export const SitebuilderActions = sitebuilderSlice.actions;

export const sitebuilderReducer = sitebuilderSlice.reducer;
